import { useQuery } from '@tanstack/react-query';
import { tenantApi } from '../api/tenantApi';

export type ActionsDisplayMode = 'actions' | 'both' | 'task-v2';

/**
 * Single source of truth for the tenant's Actions display mode (CPT-1376).
 *
 * Replaces the two independent tenant_attr booleans (isTaskV2Enabled,
 * hideV1Actions) with one enum, actionsDisplayMode ('actions' | 'both' |
 * 'task-v2'), following the same one-setting-per-tenant shape CPT-1219 used
 * for Storyblok/CMS visibility.
 *
 * Still exposes `taskV2Enabled` / `hideV1Actions` — derived from `mode` — so
 * every existing consumer (useSidebarNav, groupsConfig's hideWhenTaskV2 /
 * showWhenTaskV2 / isV1Actions gates) keeps working unchanged; only where
 * those booleans come from has moved.
 *
 * Reads the tenant record (GET /api/tenant/:code) and inspects tenant_attr.
 * Fail-open: on error or a missing attribute, mode is 'actions' — the
 * pre-CPT-1376 default and the safest legacy behavior (matches AC6's default
 * for a tenant with no value set).
 */
export function useActionsDisplayMode(tenantCode: string): {
  mode: ActionsDisplayMode;
  taskV2Enabled: boolean;
  hideV1Actions: boolean;
  ready: boolean;
} {
  const { data, isSuccess, isError } = useQuery({
    queryKey: ['actions-display-mode', tenantCode],
    queryFn: () => tenantApi.getByCode(tenantCode),
    enabled: !!tenantCode,
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const ready = isSuccess || isError;
  const raw = data?.tenant_attr?.['actionsDisplayMode'];
  const mode: ActionsDisplayMode = raw === 'both' || raw === 'task-v2' ? raw : 'actions';

  return {
    mode,
    taskV2Enabled: mode === 'both' || mode === 'task-v2',
    hideV1Actions: mode === 'task-v2',
    ready,
  };
}
